<script setup lang="ts">
import PrimeDrawer from 'primevue/drawer'
import Box from '@uiKit/elements/Box.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'

export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom' | 'full'

interface Props {
  /** v-model state. Pass `modelValue` from v-model, or `visible` to mirror the PrimeVue prop. */
  modelValue?: boolean
  /** PrimeVue-compatible alias for `modelValue`. */
  visible?: boolean
  position?: DrawerPosition
  header?: string
  /** Panel width (any CSS length). Default `'520px'`. */
  width?: string
  /** Tap-outside dismisses the drawer. Default `true`. */
  dismissable?: boolean
  /** Show the X close icon in the header. Default `true`. */
  showCloseIcon?: boolean
  /**
   * Show the default skeleton loader (header avatar+lines + body blocks).
   * Override with the `#loading-header` and `#loading` slots if a screen
   * needs bespoke skeletons. Tap-outside is auto-disabled while loading.
   */
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  position: 'right',
  width: '520px',
  dismissable: true,
  showCloseIcon: true,
  loading: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:visible', value: boolean): void
  (e: 'hide'): void
}>()

const pt = {
  root: {
    class: 'app-drawer__panel',
    style: { width: props.width, maxWidth: '100vw' },
  },
  header: { class: 'app-drawer__header' },
  content: { class: 'app-drawer__content' },
  footer: { class: 'app-drawer__footer' },
}

// Emit both `update:modelValue` and `update:visible` so callers can use
// either v-model form. PrimeVue's Drawer emits update:visible; we
// re-emit it as both for kit consistency with Dialog.
const onHide = () => {
  emit('update:modelValue', false)
  emit('update:visible', false)
  emit('hide')
}
</script>

<template>
  <PrimeDrawer
    :visible="modelValue || visible"
    :position="position"
    :header="header"
    :dismissable="dismissable && !loading"
    :showCloseIcon="showCloseIcon"
    :pt="pt"
    :block-scroll="true"
    @update:visible="(v: boolean) => !v && onHide()"
  >
    <!-- Header: loading takes precedence over the consumer-supplied #header -->
    <template v-if="loading" #header>
      <slot name="loading-header">
        <Box flex align="center" gap="3">
          <Skeleton width="40px" height="40px" border-radius="50%" />
          <Box flex direction="col" gap="1">
            <Skeleton width="160px" height="18px" />
            <Skeleton width="200px" height="13px" />
          </Box>
        </Box>
      </slot>
    </template>
    <template v-else-if="$slots.header" #header>
      <slot name="header" />
    </template>

    <!-- Body: same precedence rule — loading wins, override via #loading slot -->
    <slot v-if="loading" name="loading">
      <Box flex direction="col" gap="3" padding="4">
        <Skeleton height="80px" />
        <Skeleton height="120px" />
        <Skeleton height="60px" />
      </Box>
    </slot>
    <slot v-else />

    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </PrimeDrawer>
</template>
